import { action } from '@ember/object';
import Service, { type Registry as Services, service } from '@ember/service';
import { task } from 'ember-concurrency';
import { openUrl } from 'frontend-utils/utils/url/open-url';
import type { SmartInsightsUpsell } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';

export type SmartInsightsUpsellPath = 'pricing' | 'performance';

const EXPLORE_URLS: Record<SmartInsightsUpsellPath, string> = {
  pricing: 'https://www.mylighthouse.com/platform/pricing',
  performance: 'https://www.mylighthouse.com/platform/performance',
};

export default class SmartInsightsUpsellService extends Service {
  @service declare access: Services['access'];
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare store: Services['store'];
  @service declare usageAnalytics: Services['usage-analytics'];

  saveUpsellRecordTask = task(
    { drop: true },
    async (record: SmartInsightsUpsell): Promise<boolean> => {
      try {
        await record.save();
        return true;
      } catch (error) {
        this.errorTracking.captureException(error);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.alerting.showErrorAlert();
        return false;
      }
    },
  );

  upsellApiResponseTask = task(
    { restartable: true },
    async (): Promise<SmartInsightsUpsell | null> => {
      const subscriptionId = this.hotelAndMarketService.hotel.id;

      if (!subscriptionId) {
        return null;
      }

      try {
        return await this.store.queryRecord<SmartInsightsUpsell>(
          'smart-insights/upsell',
          {
            typeKey: 'smartinsights/upsell',
            subscription_id: subscriptionId,
          },
        );
      } catch (error) {
        this.errorTracking.captureException(error);
        throw error;
      }
    },
  );

  get hasRequestedTrial(): boolean {
    return this.upsellRecord?.trial365Requested ?? false;
  }

  get upsellPath(): SmartInsightsUpsellPath {
    return this.access.hasBenchmarkInsight || this.access.hasRevenueInsight
      ? 'performance'
      : 'pricing';
  }

  get upsellRecord(): SmartInsightsUpsell | null {
    return this.upsellApiResponseTask.last?.value ?? null;
  }

  @action
  async onClickGetInTouch(): Promise<void> {
    const subscriptionId = this.hotelAndMarketService.hotel.id;

    if (!subscriptionId) {
      return;
    }

    const record: SmartInsightsUpsell =
      this.upsellRecord ??
      this.store.push({
        data: {
          type: 'smart-insights/upsell',
          id: subscriptionId,
          attributes: { trial365Requested: false },
        },
      });

    record.trial365Requested = true;

    const saved = await this.saveUpsellRecordTask.perform(record);

    if (!saved) {
      // Roll back the optimistic update so the accordion stays in the
      // default state and the user can try again.
      record.trial365Requested = false;
      return;
    }

    this.usageAnalytics.track('Upsell Got In Touch', {
      eventTrigger: 'smart-insights-spv-upsell',
      product: 'smart-insights',
      upsellPath: this.upsellPath,
    });
  }

  @action
  openMarketingPage(): void {
    openUrl(EXPLORE_URLS[this.upsellPath]);

    this.usageAnalytics.track('Upsell Marketing Page Opened', {
      eventTrigger: 'smart-insights-spv-upsell',
      product: 'smart-insights',
      upsellPath: this.upsellPath,
    });
  }
}

declare module '@ember/service' {
  interface Registry {
    'smart-insights/upsell': SmartInsightsUpsellService;
  }
}
